/**
 * @file use-live-refresh.ts
 * @module app/modules/service-request/hooks/use-live-refresh
 * @description Keeps service request badges and lists current. Reloads on the server's silent
 * onServiceRequestChanged socket event, when the browser tab becomes visible or focused again, and
 * on a timer while no live socket is connected (the local dev app runs without one, and a
 * production socket can drop).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { useEffect, useRef } from 'react';
import { useSocket } from '../../socket/core/Socket';

/** How often to reload while there is no live socket. */
export const FALLBACK_POLL_MS = 30000;

/**
 * Calls `refresh` whenever service request data may have changed, while `enabled`.
 * The latest `refresh` is always used, so callers need not memoize it.
 */
export const useLiveRefresh = (refresh: () => void, enabled: boolean) => {
  const { socket } = useSocket();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return;
    const run = () => refreshRef.current();
    const runWhenVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    const timer = window.setInterval(() => {
      if (!socket?.connected) run();
    }, FALLBACK_POLL_MS);

    socket?.on('onServiceRequestChanged', run);
    document.addEventListener('visibilitychange', runWhenVisible);
    window.addEventListener('focus', run);
    return () => {
      window.clearInterval(timer);
      socket?.off('onServiceRequestChanged', run);
      document.removeEventListener('visibilitychange', runWhenVisible);
      window.removeEventListener('focus', run);
    };
  }, [enabled, socket]);
};
